import { PageHeader } from "@/util-components/page-layout/page-layout";
import { SectionHeader } from "@/util-components/page-layout/section-header";
import { Activity, BarChart3, Building2, Ban, Zap, LayoutTemplate, UserX, AlertCircle, Sparkles, Wand2 } from "lucide-react";
import { useInternalAnalyticsDashboard } from "@/lib/api/requests/internal-analytics.requests";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from "@/ui-components/ui/card";
import { useContext, useState, useEffect } from "react";
import { Context } from "@/util-components/root/context";

const InternalHome = () => {
  const { user } = useContext(Context);
  const { data: dashboardData, isLoading } = useInternalAnalyticsDashboard();
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  if (isLoading) {
      return (
        <div className="h-full bg-gray-50 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
      );
  }

  const postsData = dashboardData?.postsTimeSeries.map(item => ({
      date: item.date,
      count: item.count
  }));

  const templatesData = dashboardData?.templatesTimeSeries.map(item => ({
      date: item.date,
      count: item.count
  }));
  const aiCaptionsData = dashboardData?.aiCaptionsTimeSeries.map(item => ({
      date: item.date,
      count: item.count
  }));
  const aiTokensData = dashboardData?.aiTokensTimeSeries.map(item => ({
      date: item.date,
      count: item.count
  }));

  const hours = currentTime.getHours();
  let greeting = "Guten Morgen";
  if (hours >= 12 && hours < 18) greeting = "Guten Tag";
  if (hours >= 18) greeting = "Guten Abend";

  const dateOptions: Intl.DateTimeFormatOptions = {
    weekday: "long",
    day: "numeric",
    month: "long",
  };
  const dateString = currentTime.toLocaleDateString("de-DE", dateOptions);

  return (
    <div className="h-full bg-gray-50 overflow-x-hidden">
        <PageHeader className="px-4 relative overflow-visible bg-transparent border-b-0">
             <div className="flex items-center justify-between gap-6 w-full py-2 z-10">
                <div className="flex flex-col gap-1 min-w-0">
                    <div className="flex items-center gap-2 text-sm font-medium text-gray-400 uppercase tracking-widest">
                      <span>{dateString}</span>
                    </div>
                    <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 mt-1">
                        {greeting}, {user?.firstName} 👋
                    </h1>
                </div>
             </div>
        </PageHeader>

        <div className="space-y-12 pb-12 p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto w-full">
            {/* KPI Cards */}
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Gesamt</CardTitle>
                        <Building2 className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{dashboardData?.totalOrganizations || 0}</div>
                        <p className="text-xs text-muted-foreground">Alle registrierten Vereine</p>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Aktive Abos</CardTitle>
                        <Activity className="h-4 w-4 text-green-500" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{dashboardData?.activeOrganizations || 0}</div>
                        <p className="text-xs text-muted-foreground">Laufende Verträge</p>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Kein Abo</CardTitle>
                        <UserX className="h-4 w-4 text-orange-500" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{dashboardData?.inactiveOrganizations || 0}</div>
                        <p className="text-xs text-muted-foreground">Ohne aktiven Vertrag</p>
                    </CardContent>
                </Card>
                 <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Inaktiv (Bezahlt)</CardTitle>
                        <AlertCircle className="h-4 w-4 text-yellow-500" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{dashboardData?.payingButInactiveOrganizations || 0}</div>
                        <p className="text-xs text-muted-foreground">{'>'} 30 Tage inaktiv</p>
                    </CardContent>
                </Card>
                 <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Gesperrt</CardTitle>
                        <Ban className="h-4 w-4 text-red-500" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{dashboardData?.blockedOrganizations || 0}</div>
                         <p className="text-xs text-muted-foreground">Durch Admin gesperrt</p>
                    </CardContent>
                </Card>
            </div>

            <div className="space-y-6">
                <SectionHeader
                    icon={BarChart3}
                    title="Aktivitäts-Übersicht"
                    description="Erstellte Posts und Templates in den letzten 30 Tagen."
                />
                
                <div className="grid gap-6 md:grid-cols-2">
                    <Card className="rounded-2xl border border-gray-100 shadow-sm overflow-hidden flex flex-col h-full transition-all hover:shadow-md hover:border-gray-200">
                        <CardHeader className="px-6 py-4 border-b border-gray-50 bg-gray-50/50">
                            <div className="flex items-center gap-2">
                                <Zap className="h-4 w-4 text-blue-600" />
                                <CardTitle className="font-semibold text-gray-900">Neue Posts (30 Tage)</CardTitle>
                            </div>
                        </CardHeader>
                        <CardContent className="h-[300px] p-6">
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={postsData}>
                                    <defs>
                                        <linearGradient id="colorPosts" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#2563eb" stopOpacity={0.1}/>
                                            <stop offset="95%" stopColor="#2563eb" stopOpacity={0}/>
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
                                    <XAxis 
                                        dataKey="date" 
                                        tickFormatter={(val) => new Date(val).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}
                                        minTickGap={30}
                                        tick={{fontSize: 12, fill: '#9ca3af'}}
                                        axisLine={false}
                                        tickLine={false}
                                    />
                                    <YAxis 
                                        tick={{fontSize: 12, fill: '#9ca3af'}} 
                                        axisLine={false}
                                        tickLine={false}
                                    />
                                    <Tooltip 
                                        labelFormatter={(val) => new Date(val).toLocaleDateString('de-DE')}
                                        contentStyle={{ borderRadius: '12px', border: '1px solid #f3f4f6', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }}
                                    />
                                    <Area type="monotone" dataKey="count" stroke="#2563eb" strokeWidth={2} fillOpacity={1} fill="url(#colorPosts)" />
                                </AreaChart>
                            </ResponsiveContainer>
                        </CardContent>
                    </Card>

                     <Card className="rounded-2xl border border-gray-100 shadow-sm overflow-hidden flex flex-col h-full transition-all hover:shadow-md hover:border-gray-200">
                        <CardHeader className="px-6 py-4 border-b border-gray-50 bg-gray-50/50">
                            <div className="flex items-center gap-2">
                                <LayoutTemplate className="h-4 w-4 text-green-600" />
                                <CardTitle className="font-semibold text-gray-900">Neue Templates (30 Tage)</CardTitle>
                            </div>
                        </CardHeader>
                        <CardContent className="h-[300px] p-6">
                             <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={templatesData}>
                                    <defs>
                                        <linearGradient id="colorTemplates" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#16a34a" stopOpacity={0.1}/>
                                            <stop offset="95%" stopColor="#16a34a" stopOpacity={0}/>
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
                                    <XAxis 
                                        dataKey="date" 
                                        tickFormatter={(val) => new Date(val).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}
                                        minTickGap={30}
                                        tick={{fontSize: 12, fill: '#9ca3af'}}
                                        axisLine={false}
                                        tickLine={false}
                                    />
                                    <YAxis 
                                        tick={{fontSize: 12, fill: '#9ca3af'}} 
                                        axisLine={false}
                                        tickLine={false}
                                    />
                                    <Tooltip 
                                         labelFormatter={(val) => new Date(val).toLocaleDateString('de-DE')}
                                         contentStyle={{ borderRadius: '12px', border: '1px solid #f3f4f6', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }}
                                    />
                                    <Area type="monotone" dataKey="count" stroke="#16a34a" strokeWidth={2} fillOpacity={1} fill="url(#colorTemplates)" />
                                </AreaChart>
                            </ResponsiveContainer>
                        </CardContent>
                    </Card>
                </div>
            </div>

            <div className="space-y-6">
                <SectionHeader
                    icon={Sparkles}
                    title="AI Usage"
                    description="Caption-Generierungen und Tokenverbrauch der letzten 30 Tage."
                />

                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6">
                    <Card>
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                            <CardTitle className="text-sm font-medium">Caption-Generierungen</CardTitle>
                            <Sparkles className="h-4 w-4 text-blue-500" />
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold">{dashboardData?.aiCaptionGenerations || 0}</div>
                            <p className="text-xs text-muted-foreground">Alle Caption-Anfragen</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                            <CardTitle className="text-sm font-medium">Auto-Generierungen</CardTitle>
                            <Wand2 className="h-4 w-4 text-cyan-500" />
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold">{dashboardData?.aiCaptionAutoGenerations || 0}</div>
                            <p className="text-xs text-muted-foreground">Automatisch im Post-Flow</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                            <CardTitle className="text-sm font-medium">Verbrauchte Tokens</CardTitle>
                            <Zap className="h-4 w-4 text-purple-500" />
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold">{dashboardData?.aiCaptionTokens || 0}</div>
                            <p className="text-xs text-muted-foreground">Summe aller Caption-Requests</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                            <CardTitle className="text-sm font-medium">Tokens pro Request</CardTitle>
                            <BarChart3 className="h-4 w-4 text-emerald-500" />
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold">{dashboardData?.aiCaptionAverageTokens || 0}</div>
                            <p className="text-xs text-muted-foreground">Durchschnittlicher Caption-Aufwand</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                            <CardTitle className="text-sm font-medium">Tokens pro User</CardTitle>
                            <UserX className="h-4 w-4 text-slate-500" />
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold">{dashboardData?.aiCaptionAverageTokensPerUser || 0}</div>
                            <p className="text-xs text-muted-foreground">Durchschnittlicher Verbrauch pro Nutzer</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                            <CardTitle className="text-sm font-medium">Tokens pro Post</CardTitle>
                            <LayoutTemplate className="h-4 w-4 text-slate-500" />
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold">{dashboardData?.aiCaptionAverageTokensPerPost || 0}</div>
                            <p className="text-xs text-muted-foreground">Durchschnittlicher Verbrauch pro Post</p>
                        </CardContent>
                    </Card>
                </div>

                <div className="grid gap-6 md:grid-cols-2">
                    <Card className="rounded-2xl border border-gray-100 shadow-sm overflow-hidden flex flex-col h-full transition-all hover:shadow-md hover:border-gray-200">
                        <CardHeader className="px-6 py-4 border-b border-gray-50 bg-gray-50/50">
                            <div className="flex items-center gap-2">
                                <Sparkles className="h-4 w-4 text-blue-600" />
                                <CardTitle className="font-semibold text-gray-900">Caption-Generierungen</CardTitle>
                            </div>
                        </CardHeader>
                        <CardContent className="h-[300px] p-6">
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={aiCaptionsData}>
                                    <defs>
                                        <linearGradient id="colorAiCaptions" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.12}/>
                                            <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0}/>
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
                                    <XAxis
                                        dataKey="date"
                                        tickFormatter={(val) => new Date(val).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}
                                        minTickGap={30}
                                        tick={{fontSize: 12, fill: '#9ca3af'}}
                                        axisLine={false}
                                        tickLine={false}
                                    />
                                    <YAxis tick={{fontSize: 12, fill: '#9ca3af'}} axisLine={false} tickLine={false} />
                                    <Tooltip
                                        labelFormatter={(val) => new Date(val).toLocaleDateString('de-DE')}
                                        contentStyle={{ borderRadius: '12px', border: '1px solid #f3f4f6', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }}
                                    />
                                    <Area type="monotone" dataKey="count" stroke="#0ea5e9" strokeWidth={2} fillOpacity={1} fill="url(#colorAiCaptions)" />
                                </AreaChart>
                            </ResponsiveContainer>
                        </CardContent>
                    </Card>

                    <Card className="rounded-2xl border border-gray-100 shadow-sm overflow-hidden flex flex-col h-full transition-all hover:shadow-md hover:border-gray-200">
                        <CardHeader className="px-6 py-4 border-b border-gray-50 bg-gray-50/50">
                            <div className="flex items-center gap-2">
                                <Zap className="h-4 w-4 text-purple-600" />
                                <CardTitle className="font-semibold text-gray-900">Tokenverbrauch</CardTitle>
                            </div>
                        </CardHeader>
                        <CardContent className="h-[300px] p-6">
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={aiTokensData}>
                                    <defs>
                                        <linearGradient id="colorAiTokens" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#7c3aed" stopOpacity={0.12}/>
                                            <stop offset="95%" stopColor="#7c3aed" stopOpacity={0}/>
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
                                    <XAxis
                                        dataKey="date"
                                        tickFormatter={(val) => new Date(val).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}
                                        minTickGap={30}
                                        tick={{fontSize: 12, fill: '#9ca3af'}}
                                        axisLine={false}
                                        tickLine={false}
                                    />
                                    <YAxis tick={{fontSize: 12, fill: '#9ca3af'}} axisLine={false} tickLine={false} />
                                    <Tooltip
                                        labelFormatter={(val) => new Date(val).toLocaleDateString('de-DE')}
                                        contentStyle={{ borderRadius: '12px', border: '1px solid #f3f4f6', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }}
                                    />
                                    <Area type="monotone" dataKey="count" stroke="#7c3aed" strokeWidth={2} fillOpacity={1} fill="url(#colorAiTokens)" />
                                </AreaChart>
                            </ResponsiveContainer>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    </div>
  );
};

export default InternalHome;
